// generatedAppSandbox.js — the switch that sends AI-generated apps to the
// opaque sandbox frame (ISOLATED-RUNTIME-PLAN §8.1, M2).
//
// The builders (functions/index.js) stay the one door: the AST gate runs in
// the shell first, on the exact text the frame will evaluate. With the flag
// on, a passed page becomes a SandboxedApp def — plain data the frame boots
// from — and nothing generated evaluates in the shell realm. With the flag
// off, the builders mount in the shell as before (the fallback until the
// 10-10 review).

import { parseExpressionAt } from 'acorn'
import _wsShared from '@symbo-ls/workspace-shared'
import {
  auditGeneratedAppSource,
  GENERATED_APP_BLOCKED,
  generatedAppViolationHint
} from './generatedAppContainment.js'
import { SANDBOX_COMPONENTS_DROPPED } from './sandboxComponents.js'
import {
  GENERATED_APP_ROOT_DENY_KEYS,
  GENERATED_APP_ROOT_DENY_PATTERN
} from './generatedAppRules.js'

// The flag: `appsSandbox` in the shared flag registry (featureFlagsData.js,
// defaultOn: false), read through the registry's own featureEnabled — on only
// for a stored boolean `true` (the persisted workspace bag first, then the
// root mirror, then the admin's cached copy).
export const GENERATED_APP_SANDBOX_FLAG = 'appsSandbox'
// deferred-call wrapper (the bare shared-library import, see featureFlags.js)
const shellFeatureEnabled = (...args) => _wsShared.functions.featureEnabled(...args)
const _sandboxFlagDef = (key) =>
  _wsShared.functions.featureFlagDef ? _wsShared.functions.featureFlagDef(key) : null

// FAIL CLOSED: featureEnabled reads an UNREGISTERED key as ON, so the reader
// first requires the registry entry (and its default-off declaration).
export const generatedAppSandboxOn = (root) => {
  if (!root || typeof root !== 'object') return false
  const def = _sandboxFlagDef(GENERATED_APP_SANDBOX_FLAG)
  if (!def || def.defaultOn !== false) return false
  return shellFeatureEnabled(root, GENERATED_APP_SANDBOX_FLAG) === true
}

// Normalize an entity list to bare lowercase names, deduped (the shape the
// host's manifest check expects). The list itself comes from
// sandboxWrapperEntities below, never from a caller.
export const sandboxManifestEntities = (entities) => {
  const out = []
  for (const e of Array.isArray(entities) ? entities : []) {
    const s = typeof e === 'string' ? e.trim() : ''
    const dot = s.lastIndexOf('.')
    const bare = (dot >= 0 ? s.slice(dot + 1) : s).toLowerCase()
    if (bare && !out.includes(bare)) out.push(bare)
  }
  return out
}

// THE manifest of a sandboxed page (aib 2026-10-02, ROW-ENTITIES.md): no stored
// row carries `entities` and the server drops a client-sent list, so the data
// surface is what the page's OWN source declares — the `entities: [...]` key of
// the top-level object (the `{ entities, page }` wrapper, or a bare page
// literal's own key). Read from the syntax tree of the AUDITED text (acorn, as
// the gate; nothing evaluates): string literals and `{ name | entity | from }`
// objects of string literals; anything computed is ignored. Names are bare
// (`ns.name` -> `name`), lower case; a root deny key / deny-pattern name is
// dropped. No declaration -> [] (fail closed: no data access). The caller's
// list is never read.
const DENY_KEYS = new Set(GENERATED_APP_ROOT_DENY_KEYS)
const DENY_RE = new RegExp(GENERATED_APP_ROOT_DENY_PATTERN, 'i')

const keyName = (prop) =>
  !prop.computed && prop.key
    ? prop.key.type === 'Identifier'
      ? prop.key.name
      : typeof prop.key.value === 'string'
        ? prop.key.value
        : null
    : null

const stringOf = (node) =>
  node && node.type === 'Literal' && typeof node.value === 'string' ? node.value : null

const entryName = (node) => {
  const lit = stringOf(node)
  if (lit !== null) return lit
  if (node && node.type === 'ObjectExpression') {
    for (const k of ['name', 'entity', 'from']) {
      const prop = node.properties.find((p) => p.type === 'Property' && keyName(p) === k)
      const v = prop && stringOf(prop.value)
      if (v !== null && v !== undefined) return v
    }
  }
  return null
}

export const sandboxWrapperEntities = (expr) => {
  let ast
  try {
    ast = parseExpressionAt(String(expr || ''), 0, { ecmaVersion: 'latest' })
  } catch (_) {
    return []
  }
  while (ast && ast.type === 'ParenthesizedExpression') ast = ast.expression
  if (!ast || ast.type !== 'ObjectExpression') return []
  const prop = ast.properties.find((p) => p.type === 'Property' && keyName(p) === 'entities')
  if (!prop || !prop.value || prop.value.type !== 'ArrayExpression') return []
  const out = new Set()
  for (const el of prop.value.elements) {
    const raw = entryName(el)
    if (!raw) continue
    const dot = raw.lastIndexOf('.')
    const bare = (dot >= 0 ? raw.slice(dot + 1) : raw).trim().toLowerCase()
    if (!bare || DENY_KEYS.has(bare) || DENY_RE.test(bare)) continue
    out.add(bare)
  }
  return [...out].sort()
}

// The frame renders the runtime's built-ins and the PROVEN house components
// only (sandboxComponents.js). A page that names a house component the frame
// cannot render — an `extends` / `childExtends` value or a bare PascalCase key
// (the part before `_`) — would mount blank there, so the sandbox path
// refuses it at build time. Every allowlisted house component is classified,
// so the dropped set is the complete list of such names. Runs on the audited
// text, after the app-rules gate passed (that module stays a byte copy).
const DROPPED = new Set(Object.keys(SANDBOX_COMPONENTS_DROPPED))

export const sandboxComponentRefusals = (expr) => {
  let ast
  try {
    ast = parseExpressionAt(String(expr || ''), 0, { ecmaVersion: 'latest' })
  } catch (_) {
    return []
  }
  const out = []
  const flag = (node, name) =>
    out.push({ rule: 'component_not_in_sandbox', at: node.start, detail: name })
  const visit = (node) => {
    if (!node || typeof node.type !== 'string') return
    if (node.type === 'ObjectExpression') {
      for (const p of node.properties) {
        if (p.type !== 'Property') continue
        const key = keyName(p)
        if (key === 'extends' || key === 'childExtends') {
          const vals = p.value.type === 'ArrayExpression' ? p.value.elements : [p.value]
          for (const v of vals) {
            const n = stringOf(v)
            if (n !== null && n !== undefined) {
              const base = n.split('>')[0].trim()
              if (DROPPED.has(base)) flag(v, base)
            }
          }
        } else if (key && /^[A-Z]/.test(key) && DROPPED.has(key.split('_')[0])) {
          flag(p, key.split('_')[0])
        }
      }
    }
    for (const k of Object.keys(node)) {
      if (k === 'type' || k === 'start' || k === 'end') continue
      const v = node[k]
      if (Array.isArray(v)) v.forEach(visit)
      else if (v && typeof v.type === 'string') visit(v)
    }
  }
  visit(ast)
  return out
}

// The def a viewer mounts for a sandboxed app. Plain data only: it crosses to
// the frame in the boot message.
//   expr          — the AUDITED expression text (the frame evaluates exactly it)
//   jsonAuthored  — the frame revives string-held handlers, as the shell parser does
//   stateSeed     — fetch-target keys pre-declared, as the shell path does
//   pages         — a multi-page app: [{ route, title, expr, jsonAuthored }]
//                   (then `expr` is empty); the frame builds the nav shell
//   appLabel      — the shell's fallback brand word for an unnamed app
//   crmLeads      — `{ enabled: true }` only while the app's CRM sending is on
//                   (the frame adds the visitor line, crmLeadsVisitorLine.js)
export const sandboxAppDef = ({
  name,
  expr,
  jsonAuthored,
  entities,
  stateSeed,
  pages,
  appLabel,
  crmLeads
} = {}) => {
  const sandboxApp = {
    name: String(name || ''),
    expr: String(expr || ''),
    jsonAuthored: jsonAuthored === true,
    entities: sandboxManifestEntities(entities),
    stateSeed: stateSeed && typeof stateSeed === 'object' ? { ...stateSeed } : {}
  }
  if (Array.isArray(pages)) {
    sandboxApp.pages = pages.map((p) => ({
      route: String(p.route),
      title: String(p.title || ''),
      expr: String(p.expr),
      jsonAuthored: p.jsonAuthored === true
    }))
    sandboxApp.appLabel = String(appLabel || '')
  }
  if (crmLeads && crmLeads.enabled === true) sandboxApp.crmLeads = { enabled: true }
  return { extends: 'SandboxedApp', sandboxApp }
}

// Empty-list defaults for every `as: '~/key'` fetch target declared in an app
// source — pre-declared on the page's state so reactive readers track the key
// from first render (the same seed for the in-shell and the sandbox mount).
export const fetchTargetStateSeed = (source) => {
  const out = {}
  const re = /\bas\s*:\s*['"]~?\/?([A-Za-z_$][\w$]*)['"]/g
  let m
  while ((m = re.exec(String(source || '')))) out[m[1]] = []
  return out
}

// The top-level keys of every `s.update({ … })` / `state.update({ … })` object
// literal in an app source — the keys the page WRITES, so the page owns them.
// An undeclared key of a page's state reads through to the shell root: the
// round-4 open-house form's `agent` field read the shell's `agent: 'sdk-ai'`
// and showed it as the field's value (dev 2026-10-28, persona marcus).
export const writtenStateKeys = (source) => {
  const src = String(source || '')
  const keys = new Set()
  const re = /\b(?:s|state)\.update\(\s*\{/g
  let m
  while ((m = re.exec(src))) {
    let depth = 1
    let quote = null
    let token = ''
    let expectKey = true
    for (let i = re.lastIndex; i < src.length && depth > 0; i++) {
      const c = src[i]
      if (quote) {
        if (c === '\\') i++
        else if (c === quote) quote = null
        continue
      }
      if (c === "'" || c === '"' || c === '`') {
        quote = c
        continue
      }
      if (c === '{' || c === '[' || c === '(') depth++
      else if (c === '}' || c === ']' || c === ')') depth--
      if (depth !== 1) {
        if (depth < 1 && expectKey && token) keys.add(token)
        token = ''
        continue
      }
      if (/[\w$]/.test(c)) {
        if (expectKey) token += c
      } else if (c === ':') {
        if (expectKey && token) keys.add(token)
        token = ''
        expectKey = false
      } else if (c === ',') {
        if (expectKey && token) keys.add(token)
        token = ''
        expectKey = true
      } else if (c === '.') {
        token = ''
        expectKey = false
      } else if (!/\s/.test(c)) {
        token = ''
      }
    }
  }
  return [...keys].filter((k) => /^[A-Za-z_$][\w$]*$/.test(k))
}

// The page's own state seed: every written key declared (null) so it never
// reads through to the shell root, then the fetch targets ([] — see above).
// A key the page only READS still reaches the root, as before.
export const pageStateSeed = (source) => {
  const out = {}
  for (const k of writtenStateKeys(source)) out[k] = null
  return { ...out, ...fetchTargetStateSeed(source) }
}

// el.call('buildSandboxedAppDef', { name, source, crmLeads }) → { ok, def } | the gate's
// refusal. THE sandbox def for a single-page source: the AST gate on the exact
// text the frame evaluates, nothing evaluated here. The data surface is the
// page's own wrapper (sandboxWrapperEntities); a caller `entities` is ignored.
export function buildSandboxedAppDef(args = {}) {
  const el = this
  const a = args || {}
  const source = String(a.source || '')
  const checked = auditGeneratedAppSource.call(el, source)
  if (!checked.ok) return checked
  const refused = sandboxComponentRefusals(checked.expr)
  if (refused.length) {
    const hint = generatedAppViolationHint(refused)
    return {
      ok: false,
      blocked: true,
      error: GENERATED_APP_BLOCKED,
      violations: refused,
      ...(hint ? { hint } : {})
    }
  }
  return {
    ok: true,
    def: sandboxAppDef({
      name: a.name || '',
      expr: checked.expr,
      jsonAuthored: checked.jsonAuthored,
      entities: sandboxWrapperEntities(checked.expr),
      stateSeed: pageStateSeed(source),
      crmLeads: a.crmLeads
    })
  }
}
